// src/components/PopularList.jsx
import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { imgOrPlaceholder } from "../utils/format";
import { useTranslation } from "../i18n";
import { withLangPrefix } from "../lib/i18nRoutes";

/* --------- Translation Helpers (Internal or Import) --------- */
const DEFAULT_LANG = "en";

function cleanTranslatedString(v) {
  if (typeof v !== "string") return v;
  return v.replace(/\s*Translated\s*\([^)]+\)\s*$/i, "").trim();
}

function getTranslatedField(item, field, lang) {
  if (!item) return null;
  const L = (lang || DEFAULT_LANG).toLowerCase().split(/[-_]/)[0];
  if (Array.isArray(item.translations)) {
    const found = item.translations.find(tr => 
      (tr?.target_lang || tr?.language || tr?.lang)?.toLowerCase().startsWith(L)
    );
    if (found && found[field]) return cleanTranslatedString(found[field]);
  }
  if (item.translated?.[L]?.[field]) return cleanTranslatedString(item.translated[L][field]);
  if (item.trans?.[L]?.[field] || item.i18n?.[L]?.[field]) return cleanTranslatedString(item.trans?.[L]?.[field] || item.i18n?.[L]?.[field]);
  const suffix = `${field}_${L}`;
  if (item[suffix]) return cleanTranslatedString(item[suffix]);
  return item[field] ? cleanTranslatedString(item[field]) : null;
}

function normalizeArticleForLang(article, lang) {
  if (!article) return null;
  const tTitle = getTranslatedField(article, "title", lang);
  const tImage = getTranslatedField(article, "image", lang) ?? getTranslatedField(article, "thumbnail", lang);
  const slug = article.slug ?? article.id ?? "";
  const hrefPath = typeof article.url === "string" && article.url.startsWith("/articles/") 
    ? article.url : `/articles/${encodeURIComponent(slug)}`;

  return {
    ...article,
    title: tTitle ?? article.title ?? "Untitled",
    image_url: tImage ?? article.image ?? article.thumbnail ?? null,
    href: withLangPrefix(hrefPath, lang),
  };
}

/* --------- Professional Component --------- */
const PopularList = ({ items = [] }) => {
  const { lang, t } = useTranslation();

  const normalizedItems = useMemo(() => {
    return items.slice(0, 5).map((item) => normalizeArticleForLang(item, lang));
  }, [items, lang]);

  if (!normalizedItems.length) {
    return <div className="py-4 text-center text-muted small">{t("no_popular_posts") || "No trending stories yet."}</div>;
  }

  return (
    <div className="popular-list-container">
      {normalizedItems.map((a, index) => (
        <article key={a.id || a.slug} className="popular-item-row group">
          <Link to={a.href} className="popular-link-wrapper">
            {/* Rank Number - Modern Accent */}
            <div className="rank-indicator">
              0{index + 1}
            </div>
            
            <div className="popular-content">
              <h4 className="popular-title">
                {a.title}
              </h4>
              
              <div className="popular-meta">
                <span className="read-more-link">
                  {t("read_story") || "Read Story"}
                  <i className={`bi bi-arrow-${lang === 'ar' ? 'left' : 'right'} ms-1`} />
                </span>
              </div>
            </div>

            {/* Thumbnail with Hover Effect */}
            <div className="popular-thumb-container">
              <img
                src={imgOrPlaceholder(a.image_url, 120, 120)}
                alt={a.title}
                className="popular-img"
                loading="lazy"
              />
            </div>
          </Link>
          {index !== normalizedItems.length - 1 && <hr className="popular-divider" />}
        </article>
      ))}

      <style>{`
        .popular-list-container {
          display: flex;
          flex-direction: column;
        }

        .popular-item-row {
          position: relative;
          padding: 12px 0;
          transition: all 0.3s ease;
        }

        .popular-link-wrapper {
          display: flex;
          align-items: flex-start;
          gap: 16px;
          text-decoration: none !important;
          color: inherit;
        }

        /* Rank Number styling */
        .rank-indicator {
          font-family: 'Inter', sans-serif;
          font-weight: 800;
          font-size: 1.25rem;
          color: rgba(0,0,0,0.1); /* Subtle ghost number */
          line-height: 1;
          padding-top: 4px;
          transition: color 0.3s ease;
        }

        [dir="rtl"] .rank-indicator {
            order: 3;
        }

        .popular-content {
          flex: 1;
          min-width: 0;
        }

        .popular-title {
          font-size: 0.95rem;
          font-weight: 700;
          line-height: 1.35;
          margin-bottom: 6px;
          color: #212529;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          transition: color 0.2s ease;
        }

        .popular-meta {
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .read-more-link {
          color: var(--bs-primary, #0d6efd);
          opacity: 0.8;
          transition: opacity 0.2s;
        }

        .popular-thumb-container {
          width: 70px;
          height: 70px;
          flex-shrink: 0;
          border-radius: 8px;
          overflow: hidden;
          background: #f8f9fa;
        }

        .popular-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s cubic-bezier(0.165, 0.84, 0.44, 1);
        }

        /* Hover States */
        .popular-item-row:hover .popular-title {
          color: var(--bs-primary, #0d6efd);
        }

        .popular-item-row:hover .rank-indicator {
          color: var(--bs-primary, #0d6efd);
          opacity: 0.4;
        }

        .popular-item-row:hover .popular-img {
          transform: scale(1.1);
        }

        .popular-item-row:hover .read-more-link {
          opacity: 1;
          text-decoration: underline;
        }

        .popular-divider {
          margin: 12px 0 0 0;
          opacity: 0.08;
        }

        /* RTL Logic */
        [dir="rtl"] .popular-link-wrapper {
          text-align: right;
        }
      `}</style>
    </div>
  );
};

export default PopularList;